/**
 * item-deadline — fourth adversarial review, MEDIUM #2. A FIXED per-item
 * deadline (the third adversarial review's `OVERALL_ITEM_DEADLINE_MS`,
 * 150s) meant an item with the 4 mandatory angles + N incident photos, on a
 * slow link, re-uploading everything on EVERY attempt, could simply never
 * finish within the window — it would never sync at all. Scales the
 * deadline with the item's own photo count instead: enough time for a slow
 * connection to upload every photo, capped so a single item can never hold
 * the drainer's single-flight lock indefinitely.
 *
 * Pure domain math (`offline/domain`, no Dexie/fetch) — imported by BOTH
 * `checkin/infrastructure/build-real-checkin-context.ts` (to size the
 * actual per-item `AbortSignal` deadline) and
 * `offline/application/foreground-drainer.ts` (to size
 * `STUCK_SYNCING_RESET_THRESHOLD_MS`, fourth adversarial review LOW #3, so
 * a stuck-syncing reset can never fire while a legitimately still-running
 * send — up to the max possible deadline — is in flight). A shared,
 * dependency-free home avoids `foreground-drainer.ts` (offline/application)
 * having to import from `checkin/infrastructure` — the WRONG direction for
 * this codebase's layering (checkin depends on offline, never the reverse).
 */

/** Fixed cost per item, regardless of photo count (form fields, createTicket itself). */
export const ITEM_DEADLINE_BASE_MS = 60_000;

/** Additional budget per photo (mandatory angle OR incident), each its own request. */
export const ITEM_DEADLINE_PER_PHOTO_MS = 30_000;

/** Absolute ceiling — a single item can never hold the drainer's lock longer than this. */
export const ITEM_DEADLINE_MAX_MS = 600_000;

/**
 * computeItemDeadlineMs(photoCount) — `photoCount` is the TOTAL number of
 * photo requests this item's sync attempt will make (mandatory
 * `photoRefs.length` + incident `incidentPhotoRefs.length`) — every ref
 * counted, even ones whose key might end up being REUSED from a prior
 * attempt (finding #2's key-reuse fix) rather than re-uploaded: the
 * deadline must cover the WORST case (a fresh attempt with nothing cached
 * yet), not the best case.
 */
export function computeItemDeadlineMs(photoCount: number): number {
  return Math.min(
    ITEM_DEADLINE_BASE_MS + ITEM_DEADLINE_PER_PHOTO_MS * photoCount,
    ITEM_DEADLINE_MAX_MS,
  );
}
